/**
 * Wallet Service
 *
 * Type-safe wallet management service using @monorepo/shared schemas
 */

import type {
  WalletStatus,
  WalletTransactionType,
} from '@monorepo/shared/schemas/common'
import {
  type CreateRecurringRule,
  CreateRecurringRuleSchema,
  type CreateUsageRestriction,
  CreateUsageRestrictionSchema,
  type CreateWallet,
  type CreateWalletAlert,
  CreateWalletAlertSchema,
  CreateWalletSchema,
  type CreateWalletTransaction,
  CreateWalletTransactionSchema,
  type PaginatedTransactionsResponse,
  PaginatedTransactionsResponseSchema,
  type PaginatedWalletsResponse,
  PaginatedWalletsResponseSchema,
  RecurringRulesArraySchema,
  type RecurringTransactionRule,
  RecurringTransactionRuleSchema,
  type TransactionQuery,
  type UpdateRecurringRule,
  UpdateRecurringRuleSchema,
  type UpdateUsageRestriction,
  UpdateUsageRestrictionSchema,
  type UpdateWallet,
  type UpdateWalletAlert,
  UpdateWalletAlertSchema,
  UpdateWalletSchema,
  type UsageRestriction,
  UsageRestrictionSchema,
  UsageRestrictionsArraySchema,
  type Wallet,
  type WalletAlert,
  WalletAlertSchema,
  WalletAlertsArraySchema,
  type WalletBalanceResponse,
  WalletBalanceResponseSchema,
  type WalletQuery,
  type WalletResponse,
  WalletResponseSchema,
  type WalletTransaction,
  WalletTransactionSchema,
} from '@monorepo/shared/schemas/wallets'
import { del, get, patch, post } from '@/lib/api-client'

class WalletService {
  private readonly baseUrl = '/wallets'

  /**
   * Get wallet by customer ID
   */
  async getWalletByCustomerId(customerId: string): Promise<WalletResponse> {
    return get(`${this.baseUrl}/customer/${customerId}`, WalletResponseSchema)
  }

  /**
   * Get wallet by wallet ID
   */
  async getWalletById(walletId: string): Promise<WalletResponse> {
    return get(`${this.baseUrl}/${walletId}`, WalletResponseSchema)
  }

  /**
   * List all wallets (with pagination)
   */
  async listWallets(query?: WalletQuery): Promise<PaginatedWalletsResponse> {
    const queryParams = new URLSearchParams()

    if (query?.customer_id) queryParams.append('customer_id', query.customer_id)
    if (query?.organization_id)
      queryParams.append('organization_id', query.organization_id)
    if (query?.status) queryParams.append('status', query.status)
    if (query?.search) queryParams.append('search', query.search)
    if (query?.page) queryParams.append('page', query.page.toString())
    if (query?.limit) queryParams.append('limit', query.limit.toString())

    const url = `${this.baseUrl}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

    return get(url, PaginatedWalletsResponseSchema)
  }

  /**
   * Create a new wallet
   */
  async createWallet(data: CreateWallet): Promise<WalletResponse> {
    const validated = CreateWalletSchema.parse(data)

    return post(this.baseUrl, validated, WalletResponseSchema)
  }

  /**
   * Update wallet settings
   */
  async updateWallet(
    walletId: string,
    data: UpdateWallet
  ): Promise<WalletResponse> {
    const validated = UpdateWalletSchema.parse(data)

    return patch(`${this.baseUrl}/${walletId}`, validated, WalletResponseSchema)
  }

  /**
   * Delete a wallet
   */
  async deleteWallet(walletId: string): Promise<void> {
    await del(`${this.baseUrl}/${walletId}`)
  }

  /**
   * Get current wallet balance
   */
  async getWalletBalance(walletId: string): Promise<WalletBalanceResponse> {
    return get(
      `${this.baseUrl}/${walletId}/balance`,
      WalletBalanceResponseSchema
    )
  }

  /**
   * Add credits to wallet
   */
  async addCredits(
    walletId: string,
    transactionData: CreateWalletTransaction
  ): Promise<WalletTransaction> {
    // Ensure transaction_type is set to 'credit'
    const creditData: CreateWalletTransaction = {
      ...transactionData,
      transaction_type: 'credit',
    }

    const validated = CreateWalletTransactionSchema.parse(creditData)

    return post(
      `${this.baseUrl}/${walletId}/transactions`,
      validated,
      WalletTransactionSchema
    )
  }

  /**
   * Deduct credits from wallet
   */
  async deductCredits(
    walletId: string,
    amount: number,
    description?: string
  ): Promise<WalletTransaction> {
    const transactionData: CreateWalletTransaction = {
      transaction_type: 'debit',
      amount,
      description,
      metadata: {},
    }

    const validated = CreateWalletTransactionSchema.parse(transactionData)

    return post(
      `${this.baseUrl}/${walletId}/transactions`,
      validated,
      WalletTransactionSchema
    )
  }

  /**
   * Get wallet transactions
   */
  async getWalletTransactions(
    walletId: string,
    query?: TransactionQuery
  ): Promise<PaginatedTransactionsResponse> {
    const queryParams = new URLSearchParams()

    if (query?.transaction_type)
      queryParams.append('transaction_type', query.transaction_type)
    if (query?.status) queryParams.append('status', query.status)
    if (query?.from_date) queryParams.append('from_date', query.from_date)
    if (query?.to_date) queryParams.append('to_date', query.to_date)
    if (query?.page) queryParams.append('page', query.page.toString())
    if (query?.limit) queryParams.append('limit', query.limit.toString())

    const url = `${this.baseUrl}/${walletId}/transactions${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

    return get(url, PaginatedTransactionsResponseSchema)
  }

  /**
   * Get single transaction
   */
  async getTransaction(
    walletId: string,
    transactionId: string
  ): Promise<WalletTransaction> {
    return get(
      `${this.baseUrl}/${walletId}/transactions/${transactionId}`,
      WalletTransactionSchema
    )
  }

  // ============================================================================
  // RECURRING RULES MANAGEMENT
  // ============================================================================

  /**
   * Get recurring rules for a wallet
   */
  async getRecurringRules(
    walletId: string
  ): Promise<RecurringTransactionRule[]> {
    return get(
      `${this.baseUrl}/${walletId}/recurring-rules`,
      RecurringRulesArraySchema
    )
  }

  /**
   * Create a new recurring rule
   */
  async createRecurringRule(
    walletId: string,
    data: Omit<CreateRecurringRule, 'wallet_id'>
  ): Promise<RecurringTransactionRule> {
    const validated = CreateRecurringRuleSchema.parse({
      ...data,
      wallet_id: walletId,
    })

    return post(
      `${this.baseUrl}/recurring-rules`,
      validated,
      RecurringTransactionRuleSchema
    )
  }

  /**
   * Update a recurring rule
   */
  async updateRecurringRule(
    walletId: string,
    ruleId: string,
    data: UpdateRecurringRule
  ): Promise<RecurringTransactionRule> {
    const validated = UpdateRecurringRuleSchema.parse(data)

    return patch(
      `${this.baseUrl}/${walletId}/recurring-rules/${ruleId}`,
      validated,
      RecurringTransactionRuleSchema
    )
  }

  /**
   * Delete a recurring rule
   */
  async deleteRecurringRule(walletId: string, ruleId: string): Promise<void> {
    await del(`${this.baseUrl}/${walletId}/recurring-rules/${ruleId}`)
  }

  /**
   * Toggle recurring rule active status
   */
  async toggleRecurringRule(
    walletId: string,
    ruleId: string,
    isActive: boolean
  ): Promise<RecurringTransactionRule> {
    return this.updateRecurringRule(walletId, ruleId, {
      status: isActive ? 'active' : 'paused',
    })
  }

  // ============================================================================
  // WALLET RESTRICTIONS MANAGEMENT
  // ============================================================================

  /**
   * Get restrictions for a wallet
   */
  async getRestrictions(walletId: string): Promise<UsageRestriction[]> {
    return get(
      `${this.baseUrl}/${walletId}/restrictions`,
      UsageRestrictionsArraySchema
    )
  }

  /**
   * Create a new restriction
   */
  async createRestriction(
    walletId: string,
    data: CreateUsageRestriction
  ): Promise<UsageRestriction> {
    const validated = CreateUsageRestrictionSchema.parse(data)

    return post(
      `${this.baseUrl}/${walletId}/restrictions`,
      validated,
      UsageRestrictionSchema
    )
  }

  /**
   * Update a restriction
   */
  async updateRestriction(
    walletId: string,
    restrictionId: string,
    data: UpdateUsageRestriction
  ): Promise<UsageRestriction> {
    const validated = UpdateUsageRestrictionSchema.parse(data)

    return patch(
      `${this.baseUrl}/${walletId}/restrictions/${restrictionId}`,
      validated,
      UsageRestrictionSchema
    )
  }

  /**
   * Delete a restriction
   */
  async deleteRestriction(
    walletId: string,
    restrictionId: string
  ): Promise<void> {
    await del(`${this.baseUrl}/${walletId}/restrictions/${restrictionId}`)
  }

  // ============================================================================
  // WALLET ALERTS MANAGEMENT
  // ============================================================================

  /**
   * Get wallet alerts
   */
  async getWalletAlerts(walletId: string): Promise<WalletAlert[]> {
    return get(`${this.baseUrl}/${walletId}/alerts`, WalletAlertsArraySchema)
  }

  /**
   * Create wallet alert
   */
  async createWalletAlert(
    walletId: string,
    data: CreateWalletAlert
  ): Promise<WalletAlert> {
    const validated = CreateWalletAlertSchema.parse(data)

    return post(
      `${this.baseUrl}/${walletId}/alerts`,
      validated,
      WalletAlertSchema
    )
  }

  /**
   * Update wallet alert
   */
  async updateWalletAlert(
    walletId: string,
    alertId: string,
    data: UpdateWalletAlert
  ): Promise<WalletAlert> {
    const validated = UpdateWalletAlertSchema.parse(data)

    return patch(
      `${this.baseUrl}/${walletId}/alerts/${alertId}`,
      validated,
      WalletAlertSchema
    )
  }

  /**
   * Delete wallet alert
   */
  async deleteWalletAlert(walletId: string, alertId: string): Promise<void> {
    await del(`${this.baseUrl}/${walletId}/alerts/${alertId}`)
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  /**
   * Format currency amount for display
   */
  formatAmount(amount: number, currency: string): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount)
  }

  /**
   * Check if wallet has sufficient balance for a transaction
   */
  checkSufficientBalance(wallet: Wallet, amount: number): boolean {
    return wallet.credits_balance >= amount
  }

  /**
   * Get total balance (balance + credits in currency)
   */
  getTotalBalance(wallet: Wallet): number {
    return wallet.balance
  }

  /**
   * Get wallet status display text
   */
  getWalletStatusText(status: WalletStatus): string {
    switch (status) {
      case 'active':
        return 'Active'
      case 'suspended':
        return 'Suspended'
      case 'closed':
        return 'Closed'
      default:
        return 'Unknown'
    }
  }

  /**
   * Get transaction type display text
   */
  getTransactionTypeText(type: WalletTransactionType | string): string {
    switch (type) {
      case 'credit':
        return 'Credit Added'
      case 'debit':
        return 'Credit Used'
      case 'refund':
        return 'Refund'
      case 'charge':
        return 'Charge'
      case 'adjustment':
        return 'Adjustment'
      case 'transfer':
        return 'Transfer'
      case 'reversal':
        return 'Reversal'
      default:
        return 'Unknown'
    }
  }

  /**
   * Get transaction type color for UI
   */
  getTransactionTypeColor(
    type: WalletTransactionType | string
  ): 'green' | 'red' | 'blue' | 'gray' {
    switch (type) {
      case 'credit':
      case 'refund':
        return 'green'
      case 'debit':
      case 'charge':
        return 'red'
      case 'transfer':
      case 'adjustment':
        return 'blue'
      default:
        return 'gray'
    }
  }
}

export const walletService = new WalletService()
export { WalletService }

// Re-export types for convenience
export type {
  Wallet,
  WalletResponse,
  CreateWallet,
  UpdateWallet,
  PaginatedWalletsResponse,
  WalletQuery,
  WalletTransaction,
  CreateWalletTransaction,
  PaginatedTransactionsResponse,
  TransactionQuery,
  RecurringTransactionRule,
  CreateRecurringRule,
  UpdateRecurringRule,
  UsageRestriction,
  CreateUsageRestriction,
  UpdateUsageRestriction,
  WalletAlert,
  CreateWalletAlert,
  UpdateWalletAlert,
  WalletBalanceResponse,
  WalletStatus,
  WalletTransactionType,
}
